import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ShieldCheck, XCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { BrandMark } from '../components/AuthBrandPanel';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config';
import { APIErrorException } from '../services/types/errors';
import { isOmbudsman } from '../utils/rbac';

interface DeviceAuthorization {
  client_id: string;
  client_name: string;
  scope: string;
  audience: string;
  expires_at: string;
  status: string;
}

type DecisionState = 'idle' | 'approved' | 'denied';

const normalizeUserCode = (value: string): string => value.trim().toUpperCase();

const getErrorMessage = (error: unknown): string => {
  if (error instanceof APIErrorException) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Something went wrong. Please try again.';
};

const getPayloadErrorMessage = (payload: unknown, fallback: string): string => {
  if (!payload || typeof payload !== 'object') {
    return fallback;
  }
  const body = payload as { detail?: unknown; message?: unknown };
  if (typeof body.detail === 'string') {
    return body.detail;
  }
  if (body.detail && typeof body.detail === 'object') {
    const detail = body.detail as { error_description?: unknown };
    if (typeof detail.error_description === 'string') {
      return detail.error_description;
    }
  }
  if (typeof body.message === 'string') {
    return body.message;
  }
  return fallback;
};

const formatApiDateTime = (value: string): string => {
  const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/.test(value);
  const normalized = hasTimezone ? value : `${value}Z`;
  return new Date(normalized).toLocaleString();
};

const OAuthDeviceApprovalPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [userCode, setUserCode] = useState(searchParams.get('user_code') || '');
  const [authorization, setAuthorization] = useState<DeviceAuthorization | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [decisionState, setDecisionState] = useState<DecisionState>('idle');

  const canApprove = isAuthenticated && isOmbudsman(user);
  const returnTo = `${location.pathname}${location.search}`;
  const authUrl = `/auth?return_to=${encodeURIComponent(returnTo)}`;
  const scopes = useMemo(
    () => authorization?.scope.split(/\s+/).filter(Boolean) || [],
    [authorization],
  );
  const audiences = useMemo(
    () => authorization?.audience.split(/\s+/).filter(Boolean) || [],
    [authorization],
  );

  const verifyCode = useCallback(async (code: string) => {
    const normalized = normalizeUserCode(code);
    if (!normalized) {
      setAuthorization(null);
      setError('');
      return;
    }

    setIsVerifying(true);
    setError('');
    setDecisionState('idle');
    try {
      const response = await fetch(
        `${config.API_URL}/v1/oauth/device/verify?user_code=${encodeURIComponent(normalized)}`,
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(getPayloadErrorMessage(payload, 'Device code was not found.'));
      }
      setAuthorization(await response.json());
    } catch (err) {
      setAuthorization(null);
      setError(getErrorMessage(err));
    } finally {
      setIsVerifying(false);
    }
  }, []);

  useEffect(() => {
    const code = searchParams.get('user_code');
    if (code) {
      verifyCode(code);
    }
  }, [searchParams, verifyCode]);

  const submitDecision = async (approve: boolean) => {
    setIsSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/v1/oauth/device/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          user_code: normalizeUserCode(userCode),
          approve,
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(getPayloadErrorMessage(payload, 'Could not update this device request.'));
      }
      const payload = await response.json();
      setDecisionState(payload.status === 'approved' ? 'approved' : 'denied');
      setAuthorization((current) => current ? { ...current, status: payload.status } : current);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-4 py-6 md:px-6">
      <main className="mx-auto flex w-full max-w-3xl flex-col gap-5">
        <header className="flex items-center justify-between gap-4">
          <Link to="/v4/today" aria-label="Good Outcomes home">
            <BrandMark />
          </Link>
          <Badge variant="outline">Internal tool authorization</Badge>
        </header>

        <section className="space-y-2">
          <div className="flex items-center gap-2 text-primary">
            <ShieldCheck className="size-5" aria-hidden="true" />
            <p className="text-[11px] font-bold uppercase tracking-[0.05em]">OAuth Device Flow</p>
          </div>
          <h1 className="text-2xl font-extrabold tracking-[-0.02em]">Authorize Synthetic Data Generator</h1>
          <p className="max-w-2xl text-[13px] text-muted-foreground">
            Approve this request only if you started the CLI login yourself.
          </p>
        </section>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {decisionState !== 'idle' && (
          <Alert className="border-[var(--go-success)]/30 bg-card">
            <CheckCircle2 className="size-4 text-[var(--go-success)]" aria-hidden="true" />
            <AlertDescription>
              Request {decisionState}. You can return to the CLI.
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Device code</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row">
              <Input
                value={userCode}
                onChange={(event) => setUserCode(event.target.value)}
                placeholder="GO-SYNTH-1234"
                aria-label="Device code"
                className="font-mono uppercase"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => verifyCode(userCode)}
                disabled={isVerifying}
              >
                {isVerifying ? 'Checking...' : 'Check code'}
              </Button>
            </div>

            {authorization && (
              <div className="rounded-lg border border-border bg-muted/50 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                      Requesting client
                    </p>
                    <h2 className="text-[15px] font-bold">{authorization.client_name}</h2>
                    <p className="font-mono text-[11px] text-muted-foreground">{authorization.client_id}</p>
                  </div>
                  <Badge variant={authorization.status === 'pending' ? 'attention' : 'secondary'}>
                    {authorization.status}
                  </Badge>
                </div>

                <dl className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground">Scopes</dt>
                    <dd className="mt-2 flex flex-wrap gap-2">
                      {scopes.map((scope) => (
                        <Badge key={scope} variant="outline">{scope}</Badge>
                      ))}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground">Audience</dt>
                    <dd className="mt-2 flex flex-wrap gap-2">
                      {audiences.map((audience) => (
                        <Badge key={audience} variant="outline">{audience}</Badge>
                      ))}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground">Expires</dt>
                    <dd className="mt-1 text-[13px]">
                      {formatApiDateTime(authorization.expires_at)}
                    </dd>
                  </div>
                </dl>
              </div>
            )}

            {!isAuthenticated && (
              <Alert>
                <AlertDescription>
                  Sign in as an admin or ombudsman user before approving this request.
                </AlertDescription>
              </Alert>
            )}

            {isAuthenticated && !canApprove && (
              <Alert variant="destructive">
                <AlertDescription>
                  Your account cannot approve generator CLI access.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:justify-end">
              {!isAuthenticated ? (
                <Button asChild>
                  <Link to={authUrl}>Sign in to approve</Link>
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => submitDecision(false)}
                    disabled={!authorization || !canApprove || isSubmitting || decisionState !== 'idle'}
                    leftIcon={<XCircle className="size-4" aria-hidden="true" />}
                  >
                    Deny
                  </Button>
                  <Button
                    type="button"
                    onClick={() => submitDecision(true)}
                    disabled={!authorization || !canApprove || isSubmitting || decisionState !== 'idle'}
                    leftIcon={<CheckCircle2 className="size-4" aria-hidden="true" />}
                  >
                    Approve
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default OAuthDeviceApprovalPage;
